import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck, KeyRound, ArrowUpRight, ArrowLeft } from "lucide-react";
import axios from "axios";
import { Reveal } from "../lib/motion";
import { formatApiErrorDetail } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() }, { withCredentials: true });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="forgot-password-page"
      className="min-h-[75vh] bg-[#F8F9FA] bg-grid flex items-center justify-center px-5 py-14"
    >
      <Reveal className="w-full max-w-md">
        {sent ? (
          <div
            data-testid="forgot-password-sent"
            className="bg-white border-2 border-[#1A1A1A] rounded-3xl p-8 nb-shadow-lg"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[#1A1A1A] bg-[#00C2B8] nb-shadow-sm">
              <MailCheck size={22} strokeWidth={2.5} />
            </span>
            <h1 className="mt-5 font-display font-black text-3xl md:text-4xl tracking-tighter">
              Check your inbox.
            </h1>
            <p className="mt-3 text-sm font-semibold text-[#4A4A4A] leading-relaxed">
              If an account exists for <span className="text-[#1A1A1A]">{email.trim()}</span>, a
              reset link is on its way. It works once and expires in 1 hour.
            </p>
            <p className="mt-3 text-xs font-semibold text-[#4A4A4A]">
              Nothing after a few minutes? Check spam, or try again.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setSent(false)}
                data-testid="forgot-password-again"
                className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wide nb-shadow-sm nb-press"
              >
                Send again
              </button>
              <Link
                to="/login"
                data-testid="forgot-password-back-login"
                className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wide nb-shadow-sm nb-press"
              >
                Back to login <ArrowUpRight size={14} strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="forgot-password-form"
            className="bg-white border-2 border-[#1A1A1A] rounded-3xl p-8 nb-shadow-lg"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[#1A1A1A] bg-[#FFD700] nb-shadow-sm">
              <KeyRound size={22} strokeWidth={2.5} />
            </span>
            <h1 className="mt-5 font-display font-black text-3xl md:text-4xl tracking-tighter">
              Forgot your password?
            </h1>
            <p className="mt-2 text-sm font-semibold text-[#4A4A4A]">
              Enter the email on your TinkerShift client account and we'll send a link to choose a
              new one.
            </p>

            {error && (
              <p
                data-testid="forgot-password-error"
                className="mt-4 bg-[#FF007F]/10 border-2 border-[#FF007F] text-[#1A1A1A] rounded-xl px-4 py-3 text-sm font-semibold"
              >
                {error}
              </p>
            )}

            <label className="mt-5 block">
              <span className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                Email
              </span>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@school.edu.au"
                data-testid="forgot-password-email-input"
                className="mt-1.5 w-full border-2 border-[#1A1A1A] rounded-xl px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-[#FFD700]"
              />
            </label>

            <button
              type="submit"
              disabled={busy || !email}
              data-testid="forgot-password-submit"
              className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-3 font-bold uppercase tracking-wide nb-shadow-sm nb-press disabled:opacity-70"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} strokeWidth={2.5} />}
              Send reset link
            </button>

            <Link
              to="/login"
              data-testid="forgot-password-back"
              className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#0055FF] hover:text-[#FF007F] transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={2.5} /> Back to login
            </Link>
          </form>
        )}
      </Reveal>
    </div>
  );
}
